/**
 * AppContext — default values.
 *
 * Extracted so the provider file stays focused on side-effects and any
 * caller (tests, storybook, onboarding) can reach for a seed value
 * without pulling in the full provider module.
 */

import type {
  UserProfile,
  Preferences,
  NotificationSettings,
  WorkSchedule,
  WorkTimeBlock,
} from "./types";

export const emptyProfile: UserProfile = {
  userId: "",
  firstName: "",
  lastName: "",
  email: "",
  avatarUrl: "",
  birthday: null,
  birthdayConfirmed: false,
  birthdaySource: null,
  timezone: "UTC",
  isOnboarded: true, // Default to true to prevent flash before load
};

const defaultWorkDayBlock: WorkTimeBlock = {
  id: "default",
  startTime: "08:00",
  endTime: "17:00",
  location: "remote",
};

export const defaultWorkSchedule: WorkSchedule = {
  timezone: "UTC-06:00",
  days: {
    sunday:    { active: false, blocks: [{ ...defaultWorkDayBlock, id: "sun-1" }] },
    monday:    { active: false, blocks: [{ ...defaultWorkDayBlock, id: "mon-1" }] },
    tuesday:   { active: false, blocks: [{ ...defaultWorkDayBlock, id: "tue-1" }] },
    wednesday: { active: false, blocks: [{ ...defaultWorkDayBlock, id: "wed-1" }] },
    thursday:  { active: false, blocks: [{ ...defaultWorkDayBlock, id: "thu-1" }] },
    friday:    { active: false, blocks: [{ ...defaultWorkDayBlock, id: "fri-1" }] },
    saturday:  { active: false, blocks: [{ ...defaultWorkDayBlock, id: "sat-1" }] },
  },
};

export const defaultPreferences: Preferences = {
  goalPeriod: "month",
  goalType: "percent",
  goalValue: 50,
  confettiEnabled: true,
  includeWeekends: false,
  yearType: "natural",
  fiscalStartMonth: 1,
  aiSuggestionsEnabled: false,
  icsUrl: "",
  circleShareScope: "month",
  circleHiddenPeerIds: [],
};

export const defaultNotifications: NotificationSettings = {
  // Legacy
  emailReminders: true,
  pushReminders:  true,
  frequency:      "daily",
  reminderTime:   "09:00",

  // Pillar 1
  reminderEnabled:      false,
  reminderChannelEmail: true,
  reminderChannelInApp: true,

  // Pillar 2 — transactional defaults match DB defaults in notif_settings
  circleInviteEmail: true,
  circleInviteInApp: true,
  eventTaggedEmail:  true,
  eventTaggedInApp:  true,

  // Pillar 3
  digestEnabled:   true,
  digestFrequency: "weekly",
};

export const DEFAULT_DAY_TYPES = [
  { id: "office",         name: "Office",         color: "#2F6FE0", is_visible: true, base_category: "WORK_OFFICE"    as const },
  { id: "client_visit",  name: "Client Visit",   color: "#0EA5E9", is_visible: true, base_category: "WORK_EXTERNAL"  as const },
  { id: "business_travel",name: "Business Travel",color: "#06B6D4", is_visible: true, base_category: "WORK_EXTERNAL"  as const },
  { id: "vacation",      name: "Vacation",        color: "#D97706", is_visible: true, base_category: "ABSENCE"        as const },
  { id: "pto",           name: "PTO",             color: "#F59E0B", is_visible: true, base_category: "ABSENCE"        as const },
  { id: "medical_absence",name:"Medical Absence", color: "#EF4444", is_visible: true, base_category: "ABSENCE"        as const },
  { id: "other_absence", name: "Other Absence",   color: "#9CA3AF", is_visible: true, base_category: "ABSENCE"        as const },
  { id: "public_holiday",name: "Public Holiday",  color: "#10B981", is_visible: false,base_category: "SYSTEM_HOLIDAY" as const },
];
